/**
 * Searchable IANA time-zone picker (Command palette). Shared by the scheduling
 * UI (ExecutionDialog) and the Settings → timezone preference control.
 */
import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';

const FALLBACK_ZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Jerusalem', 'Asia/Tokyo',
  'Asia/Shanghai', 'Asia/Singapore', 'Asia/Dubai', 'Australia/Sydney',
];

export interface TimeZonePickerProps {
  value: string;
  onChange: (tz: string) => void;
  /**
   * Optional extra entries surfaced at the top of the list (e.g. an
   * "Auto (device timezone)" sentinel). Each `value` is passed to `onChange`
   * verbatim on select.
   */
  extraOptions?: Array<{ value: string; label: string }>;
  className?: string;
}

export function TimeZonePicker({ value, onChange, extraOptions, className }: TimeZonePickerProps) {
  const [open, setOpen] = useState(false);

  // Intl.supportedValuesOf is available in all evergreen browsers (2022+).
  const zones = useMemo<string[]>(() => {
    try {
      const sv = (Intl as any).supportedValuesOf?.('timeZone');
      if (Array.isArray(sv) && sv.length > 0) return sv;
    } catch {}
    return FALLBACK_ZONES;
  }, []);

  const currentLabel = extraOptions?.find((o) => o.value === value)?.label ?? value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('font-normal text-xs h-9 min-w-[180px] justify-between', className)}
        >
          <span className="truncate">{currentLabel}</span>
          <ChevronsUpDown className="w-3 h-3 ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[280px]">
        <Command>
          <CommandInput placeholder="Search time zones…" />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>No matching zone.</CommandEmpty>
            {extraOptions && extraOptions.length > 0 && (
              <CommandGroup>
                {extraOptions.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={o.label}
                    onSelect={() => { onChange(o.value); setOpen(false); }}
                    className="text-xs"
                  >
                    <Check className={cn('w-3 h-3 mr-2', value === o.value ? 'opacity-100' : 'opacity-0')} />
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            <CommandGroup>
              {zones.map((tz) => (
                <CommandItem
                  key={tz}
                  value={tz}
                  onSelect={() => { onChange(tz); setOpen(false); }}
                  className="text-xs"
                >
                  <Check className={cn('w-3 h-3 mr-2', value === tz ? 'opacity-100' : 'opacity-0')} />
                  {tz}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
